/**
 * Hook to sync view tab state with URL query param
 * Story 258: Navigate epics more easily
 *
 * Reads ?tab param on mount, updates URL on change via replaceState
 * (no history entry created - back button preserves expected behavior)
 */

import { useState, useCallback, useEffect } from 'react';
import type { ViewMode } from '../components/workspace/ViewTabs';

const VALID_TABS: ViewMode[] = ['backlog', 'epics'];
const DEFAULT_TAB: ViewMode = 'backlog';
const TAB_PARAM = 'tab';

function isValidTab(value: string | null): value is ViewMode {
  return value !== null && VALID_TABS.includes(value as ViewMode);
}

function getTabFromUrl(): ViewMode {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get(TAB_PARAM);
  return isValidTab(tab) ? tab : DEFAULT_TAB;
}

function updateUrlTab(tab: ViewMode): void {
  const url = new URL(window.location.href);
  if (tab === DEFAULT_TAB) {
    // Remove param when default to keep URLs clean
    url.searchParams.delete(TAB_PARAM);
  } else {
    url.searchParams.set(TAB_PARAM, tab);
  }
  window.history.replaceState({}, '', url.toString());
}

/**
 * useState-like hook that syncs ViewMode with URL ?tab param
 * @returns [currentTab, setTab] - same interface as useState
 */
export function useTabFromUrl(): [ViewMode, (tab: ViewMode) => void] {
  const [tab, setTabInternal] = useState<ViewMode>(getTabFromUrl);

  const setTab = useCallback((newTab: ViewMode) => {
    setTabInternal(newTab);
    updateUrlTab(newTab);
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      setTabInternal(getTabFromUrl());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return [tab, setTab];
}
